import { useCallback, useState } from 'react';

import { Deferrable } from './utils/deferrable';
import type {
	ModalHandler,
	WhiteboardInternalModalService,
} from './services/whiteboard-internal-modal-service';
import { type WhiteboardInternalModalProps } from './whiteboard-bridge-api-type';

type WhiteboardInternalModalState =
	| {
			isModalOpen: false;
	  }
	| {
			isModalOpen: true;
			modalProps: WhiteboardInternalModalProps;
			modalCloseDeferrable: Deferrable<void>;
	  };

export const useWhiteboardInternalModal = (
	whiteboardInternalModalService: WhiteboardInternalModalService,
) => {
	const [modalState, setModalState] = useState<WhiteboardInternalModalState>({
		isModalOpen: false,
	});

	const closeModal = useCallback(() => {
		setModalState((prevModalState) => {
			if (prevModalState.isModalOpen) {
				prevModalState.modalCloseDeferrable.resolve();
			}

			return {
				isModalOpen: false,
			};
		});
	}, []);

	const modalHandler: ModalHandler = useCallback((modalProps: WhiteboardInternalModalProps) => {
		const deferrable = new Deferrable<void>();

		setModalState((prevModalState) => {
			if (prevModalState.isModalOpen) {
				prevModalState.modalCloseDeferrable.resolve();
			}

			return {
				isModalOpen: true,
				modalProps,
				modalCloseDeferrable: deferrable,
			};
		});

		return deferrable.get();
	}, []);

	whiteboardInternalModalService.registerModalHandler(modalHandler);
	whiteboardInternalModalService.registerCloseHandler(closeModal);

	return modalState.isModalOpen
		? {
				isModalOpen: modalState.isModalOpen,
				modalProps: modalState.modalProps,
				closeModal,
			}
		: modalState;
};
